import { Select } from "src/components/shared/MantineCombobox";
import { Button as MantineButton, Menu } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconChevronDown } from "@tabler/icons-react";
import { memo, useMemo, Fragment } from "react";
import { useParams } from "react-router-dom";
import AuthLogbookModal from "src/components/shared/_Modals/AuthLogbookModal/AuthLogbookModal";
import Breadcrumbs from "src/components/shared/Breadcrumbs/Breadcrumbs";
import { Button } from "src/components/shared/Button/Button";
import { type IReportData } from "src/pages/Reports/Report/Report.types";
import { useDispatch, useSelector } from "src/redux/hooks";
import {
  resetCharts,
  setSelectedLogbook,
  toggleSelectingMode,
} from "src/redux/slices/entriesGeneratorSlice";
import { ButtonsWrapper } from "src/styled";

import CreateChartButton from "./CreateChart/CreateChartButton";
import SelectedColumnsSummary from "./CreateChart/SelectedColumnsSummary";
import ReportModeSelect from "./ReportModeSelect";

interface Props {
  data: IReportData | undefined;
  handleGenerate: (
    type: "pdf" | "spreadsheet",
    csv?: boolean,
    pdfMode?: "entries" | "charts",
  ) => void;
  isFileLoading: { pdf: boolean; spreadsheet: boolean; csv: boolean };
  fetchRestricted: (token: string) => void;
  isHtmlString?: boolean;
}

const ReportPageHeader = ({
  data,
  handleGenerate,
  isFileLoading,
  fetchRestricted,
  isHtmlString,
}: Props) => {
  const { reportId } = useParams();
  const { selectingMode, logbooks, selectedLogbook, chartsData } = useSelector(
    state => state.entriesGeneratorSlice,
  );
  const [opened, { open, close }] = useDisclosure(false);

  const dispatch = useDispatch();

  const toggleMode = () => {
    dispatch(toggleSelectingMode());
  };

  const handleReset = () => {
    dispatch(resetCharts());
  };

  const handleSelect = (lbid: string) => {
    dispatch(setSelectedLogbook(lbid));
  };

  const handlePdf = () => {
    handleGenerate("pdf");
  };

  const handlePdfEntries = () => {
    handleGenerate("pdf", false, "entries");
  };

  const handlePdfCharts = () => {
    handleGenerate("pdf", false, "charts");
  };

  const handleSpreadSheet = () => {
    handleGenerate("spreadsheet", false);
  };

  const handleCsv = () => {
    handleGenerate("spreadsheet", true);
  };

  const links = useMemo(
    () => [
      {
        title: "Home",
        href: "/",
      },
      {
        title: "Reports",
        href: "/reports",
      },
      {
        title: data?.report.type === 3 ? "Waste Declaration Report" : "Report",
        href: `/reports/report/${reportId}`,
      },
    ],
    [data, reportId],
  );

  const entries = useMemo(
    () => (Array.isArray(data?.result) ? data.result : []),
    [data?.result],
  );

  return (
    <Breadcrumbs
      border={false}
      customLinks={links}
      style={{ position: data?.isHTML ? "sticky" : "relative", top: 0 }}>
      {data && (
        <ButtonsWrapper>
          {!data.authed && data.restrictedCount > 0 && (
            <>
              <AuthLogbookModal
                opened={opened}
                close={close}
                onSuccess={fetchRestricted}
                report
              />
              <Button
                onClick={open}>{`Authorize: ${data.restrictedCount}`}</Button>
            </>
          )}
          {selectingMode ? (
            <>
              <Select
                w={300}
                data={logbooks}
                value={selectedLogbook}
                onChange={handleSelect}
              />
              <SelectedColumnsSummary />
              <CreateChartButton entries={entries} />
              <Button onClick={toggleMode}>Cancel</Button>
            </>
          ) : (
            <>
              {chartsData.length > 0 && (
                <Button onClick={handleReset}>Reset Charts</Button>
              )}
              {data?.report &&
                !data?.isHTML &&
                (data.report.type === 0 || data.report.type === 2) && (
                  <Button onClick={toggleMode}>
                    {chartsData.length > 0 ? "Edit Charts" : "Create Charts"}
                  </Button>
                )}
              {(data?.report?.type === 0 || data?.report?.type === 2) &&
                !data?.isHTML && <ReportModeSelect />}
              {(!isHtmlString || data?.report?.type === 5 || data?.isHTML) && (
                <Fragment>
                  {chartsData.length > 0 ? (
                    <Menu withinPortal>
                      <Menu.Target>
                        <MantineButton
                          size="sm"
                          variant="outline"
                          rightSection={<IconChevronDown size={14} />}
                          loading={isFileLoading.pdf}
                          disabled={
                            isFileLoading.spreadsheet || isFileLoading.csv
                          }>
                          PDF
                        </MantineButton>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={handlePdfEntries}>
                          Entries only
                        </Menu.Item>
                        <Menu.Item onClick={handlePdfCharts}>
                          Charts only
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  ) : (
                    <Button
                      onClick={handlePdf}
                      loading={isFileLoading.pdf}
                      disabled={isFileLoading.spreadsheet || isFileLoading.csv}>
                      PDF
                    </Button>
                  )}
                  {!isHtmlString && data?.report?.type !== 7 && (
                    <>
                      <Button
                        onClick={handleSpreadSheet}
                        loading={isFileLoading.spreadsheet}
                        disabled={isFileLoading.pdf || isFileLoading.csv}>
                        SpreadSheet
                      </Button>
                      <Button
                        onClick={handleCsv}
                        loading={isFileLoading.csv}
                        disabled={
                          isFileLoading.pdf || isFileLoading.spreadsheet
                        }>
                        CSV
                      </Button>
                    </>
                  )}
                </Fragment>
              )}
            </>
          )}
        </ButtonsWrapper>
      )}
    </Breadcrumbs>
  );
};

export default memo(ReportPageHeader);
